import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import toastr from 'toastr';
import { MFChannel } from '../../../Components/SDK';
import { getAppOnlineStatus, setMultipleChannelsInfo } from '../../../Slice';
import { NO_INTERNET } from '../constants';

// Helper function to filter out channels that are already in recent channels
const filterNonRecentChannels = (channels, recentChannels) => {
   return channels.filter(ch => !recentChannels.some(rc => rc.channelId === ch.channelId));
};

export function useExploreList(loadingRef = null, { initialSearch = '', isJoinedOnly = false, debounceMs = 300 } = {}) {
   const dispatch = useDispatch();
   const [channelList, setChannelList] = useState([]);
   const recentChannelsRef = useRef([]);
   const [loading, setLoading] = useState(true);
   const [searching, setSearching] = useState(false);
   const [error, setError] = useState(null);
   const [searchText, setSearchText] = useState(initialSearch);
   const [hasMore, setHasMore] = useState(true);

   const debounceTimer = useRef(null);

   const exploreChannels = async ({ createdAt = 0, search = '', isSearch = false }) => {
      if (!getAppOnlineStatus()) {
         toastr.error(NO_INTERNET);
         return;
      }

      if (isSearch) {
         setSearching(true);
      } else {
         setLoading(true);
         setTimeout(() => {
            if (loadingRef) {
               loadingRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
         }, 100);
      }

      try {
         setError(null);
         const res = await MFChannel.fetchExploreChannelList({
            search,
            isJoined: 'no',
            createdAt,
            categorySize: 20,
            channelSize: 4,
            page: 1,
         });

         const { data: { channels = [], hasNext = false } = {} } = res;
         dispatch(setMultipleChannelsInfo(channels));
         const filteredChannels = channels.filter(
            ch => !recentChannelsRef.current.some(rc => rc.channelId === ch.channelId),
         );

         setChannelList(prev => (createdAt ? [...prev, ...filteredChannels] : filteredChannels));
         setHasMore(hasNext || filteredChannels.length > 0);
      } catch (err) {
         console.error('Error fetching channels:', err);
         setError(err);
         setHasMore(false);
      } finally {
         isSearch ? setSearching(false) : setLoading(false);
      }
   };

   // Initial fetch
   useEffect(() => {
      exploreChannels({ search: initialSearch });
      setSearchText(initialSearch);
   }, [initialSearch]);

   const updateSearchText = useCallback(
      newSearch => {
         clearTimeout(debounceTimer.current);
         debounceTimer.current = setTimeout(() => {
            setHasMore(true);
            setSearchText(newSearch);
            exploreChannels({ createdAt: 0, search: newSearch, isSearch: true });
         }, debounceMs);
      },
      [debounceMs],
   );

   const loadMore = useCallback(() => {
      if (hasMore && !loading && channelList.length > 0) {
         const lastItem = channelList[channelList.length - 1];
         const createdAt = lastItem?.createdAt;
         if (createdAt) {
            exploreChannels({ createdAt, search: searchText });
         }
      }
   }, [hasMore, loading, channelList, searchText]);

   const reset = useCallback(() => {
      setHasMore(true);
      exploreChannels({ createdAt: 0, search: searchText });
   }, []);

   return {
      channelList,
      loading,
      searching,
      error,
      hasMore,
      loadMore,
      updateSearchText,
      reset,
      searchText,
   };
}
